import React, { useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Layout from "components/Layout";
import api from "utils/api";
import { buildRegistrationPayload } from "utils/registration";
import eventImage1 from "assets/images/hoffmann_events_2_hero.webp";
import eventImage2 from "assets/images/hoffmann_events_1_hero.webp";
import eventImage3 from "assets/images/hoffmann_events_5_hero.webp";
import eventImage4 from "assets/images/hoffmann_events_4_hero.webp";
import eventImage5 from "assets/images/hoffmann_events_3_hero.webp";
import eventImage6 from "assets/images/hoffmann_events_8_hero.webp";
import eventImage7 from "assets/images/hoffmann_events_7_hero.webp";
import eventImage8 from "assets/images/hoffmann_events_10_hero.webp";
import eventImage9 from "assets/images/hoffmann_events_9_hero.webp";
import eventImage10 from "assets/images/hoffmann_events_6_hero.webp";
import "styles/events.css";

const HERO_IMAGES = [
  eventImage1,
  eventImage2,
  eventImage3,
  eventImage4,
  eventImage5,
  eventImage6,
  eventImage7,
  eventImage8,
  eventImage9,
  eventImage10,
];

const OCCASION_GROUPS = [
  {
    title: "Unternehmen & Team",
    items: ["Firmenveranstaltungen und Empfänge", "Meetings und Wochenmeetings", "Kick-offs und Jahresauftakte", "Kundenveranstaltungen", "Mitarbeiterveranstaltungen", "Teambuildings und Incentives", "Networking-Events"],
  },
  {
    title: "Wissen & Austausch",
    items: ["Tagungen und Kongresse", "Schulungen und Workshops", "Seminare und Weiterbildungen"],
  },
  {
    title: "Marke & Öffentlichkeit",
    items: ["Messen und Ausstellungen", "Produktpräsentationen", "Neueröffnungen und Store-Openings", "Presse- und Medienveranstaltungen", "Roadshows", "Promotion-Aktionen"],
  },
  {
    title: "Feiern & besondere Anlässe",
    items: ["Sommerfeste", "Weihnachtsfeiern", "Jubiläen", "Hochzeiten und private Feiern", "Sport- und Lifestyle-Events", "Internationale Veranstaltungen und Geschäftsreisen"],
  },
];

const EVENT_MOMENTS = [
  {
    title: "Zum Empfang",
    text: "Deine Gäste kommen aus dem Auto, aus dem Zug, vom Flughafen oder nach einem langen Arbeitstag. Eine kurze Wake-Up-Anwendung sorgt direkt zum Einstieg für einen besonderen Empfang und einen aktivierenden Moment.",
  },
  {
    title: "Während des Events",
    text: "Nach langen Meetings, Präsentationen oder Gesprächsrunden schaffen kurze Massageeinheiten eine willkommene Unterbrechung. Die aktivierenden Impulse der Perkussionsmassage können dabei helfen, sich wieder beweglicher, lockerer und energiegeladener zu fühlen.",
  },
  {
    title: "In den Pausen",
    text: "Kaffeepause, Mittagspause oder kurze Unterbrechung: Wir integrieren uns in deinen Zeitplan und bringen neue Bewegung in den Veranstaltungstag.",
  },
  {
    title: "Vor dem nächsten Programmpunkt",
    text: "Nach mehreren Stunden Sitzen kann eine kurze Wake-Up-Massage genau der richtige Übergang zum nächsten Meeting, Workshop, Vortrag oder Abendprogramm sein.",
  },
  {
    title: "Den ganzen Tag",
    text: "Auf Wunsch begleitet unser Team deine Veranstaltung vom ersten Gast bis zum Abschluss.",
  },
];

const MASSAGE_OFFERS = [
  {
    title: "Wake-Up+",
    text: "Mehr Möglichkeiten für Gruppen, Mitarbeiter, Gäste und Partner. Besonders geeignet, wenn mehrere Personen innerhalb kurzer Zeit erreicht werden sollen.",
  },
  {
    title: "Classic+",
    text: "Unsere umfangreichere Ganzkörper-Perkussionsmassage für einen intensiveren Entspannungs- und Aktivierungsmoment. Ideal für längere Veranstaltungen oder als besonderes Angebot für ausgewählte Gäste, Mitarbeiter und Geschäftspartner.",
  },
  {
    title: "Intense+",
    text: "Unsere umfassendste Begleitung. Individuell mit persönlicher Betreuung und zusätzlichen Leistungen kombinierbar – auch für größere Gruppen, besondere Veranstaltungen und internationale Einsätze.",
  },
];

const SERVICES = [
  "Massage",
  "Personal",
  "Eventbegleitung",
  "Foto",
  "Video",
  "Social Media",
  "Drohnenaufnahmen",
  "Event-Bus",
  "Shuttle-Service",
];

const INITIAL_FORM = {
  name: "",
  company: "",
  email: "",
  phone: "",
  desiredDate: "",
  eventType: "",
  personCount: "",
  message: "",
  acceptedContact: false,
};

const EventsPage = () => {
  const location = useLocation();
  const tracking = useMemo(() => buildRegistrationPayload(location), [location]);
  const formRef = useRef(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const focusNextField = (field) => {
    const fields = Array.from(formRef.current?.querySelectorAll("[data-event-field]") || []);
    const index = fields.findIndex((control) => control.dataset.eventField === field);
    fields[index + 1]?.focus({ preventScroll: true });
  };

  const centerFocusedField = (event) => {
    const control = event.target;
    if (!control.dataset.eventField) return;
    window.requestAnimationFrame(() => {
      if (document.activeElement !== control) return;
      (control.closest("label") || control).scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "center",
      });
    });
  };

  const advanceOnCompletedBlur = (event) => {
    const control = event.target;
    const field = control.dataset.eventField;
    if (!field || field === "acceptedContact" || !String(control.value).trim() || !control.checkValidity()) return;
    if (event.relatedTarget && formRef.current?.contains(event.relatedTarget)) return;
    focusNextField(field);
  };

  const advanceOnEnter = (event) => {
    const control = event.target;
    const field = control.dataset.eventField;
    if (event.key !== "Enter" || !field || control.tagName === "TEXTAREA" || control.type === "checkbox") return;
    event.preventDefault();
    if (control.reportValidity()) focusNextField(field);
  };

  const updateForm = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
    if ((field === "desiredDate" && value) || (field === "acceptedContact" && value)) {
      window.requestAnimationFrame(() => focusNextField(field));
    }
  };

  const submitInquiry = async (payload) => {
    try {
      return await api.post("/inquiries", payload);
    } catch (error) {
      const status = error?.response?.status;
      if (status && status !== 404 && status !== 405) throw error;
      return api.post("/special-requests", payload);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback(null);
    if (!form.acceptedContact) {
      setFeedback({ type: "error", message: "Bitte bestätige den Kontakt- und Datenschutzhinweis." });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        brand: "dr-hoffmann",
        requestedOfferType: "event",
        reason: "event",
        personCount: form.personCount ? Number(form.personCount) : undefined,
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        desiredDate: form.desiredDate,
        message: [
          `Eventformat / Ort: ${form.eventType || "offen"}`,
          form.personCount && `Teilnehmerzahl: ${form.personCount}`,
          form.message.trim(),
        ].filter(Boolean).join("\n"),
        ...tracking,
      };
      const result = await submitInquiry(payload);
      if (!(result?.ok || result?.success || result?.status === 200 || result?.status === 201)) {
        throw new Error(result?.message || "Die Anfrage konnte nicht verarbeitet werden.");
      }
      setFeedback({
        type: "success",
        message: "Danke, deine Anfrage wurde übermittelt. Wir melden uns schnellstmöglich.",
      });
      setForm(INITIAL_FORM);
    } catch (error) {
      setFeedback({
        type: "error",
        message: error?.message || "Die Anfrage konnte gerade nicht übermittelt werden. Bitte versuche es erneut.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout bodyClass="events-page-body">
      <div className="events-page">
        <section className="events-hero" aria-label="Events bei Dr. Hoffmann">
          <div className="events-hero__mosaic">
            {HERO_IMAGES.map((src, index) => (
              <div className="events-hero__tile" key={index}>
                <img src={src} alt="" loading={index < 5 ? "eager" : "lazy"} decoding="async" />
              </div>
            ))}
          </div>
        </section>

        <section className="events-intro">
          <div className="events-container">
            <span className="events-eyebrow">Dr. Hoffmann Events</span>
            <h1>Dein Event. Unsere Energie.<br />Alles aus einer Hand.</h1>
            <div className="events-intro__copy">
              <p>Immer und überall ist bei Dr. Hoffmann Massage kein Werbespruch. Wir meinen es genau so: Wir begleiten dein Event in Hamburg, deutschlandweit oder international – mit unseren Massagen, unserem Team und auf Wunsch mit einem kompletten Event- und Content-Paket.</p>
              <p>Ob zehn Gäste oder eine große Veranstaltung: Du kümmerst dich um deine Gäste – wir kümmern uns um den Rest.</p>
              <p>Mit Events+ kombinieren wir Wake-Up+, Classic+ und Intense+ mit professioneller Eventbegleitung, Foto, Video, Social Media, Shuttle-Service und unserem individuell gestaltbaren Event-Bus.</p>
            </div>
          </div>
        </section>

        <section className="events-variety" aria-labelledby="events-variety-title">
          <div className="events-container">
            <h2 id="events-variety-title">Für nahezu jeden Anlass</h2>
            <p className="events-section-lead">Events+ lässt sich individuell an dein Unternehmen, deine Veranstaltung und deine Gäste anpassen. Ideal zum Beispiel für:</p>
            <div className="events-occasion-groups">
              {OCCASION_GROUPS.map((group, index) => (
                <div className="events-occasion-group" key={group.title}>
                  <span className="events-occasion-group__number" aria-hidden="true">0{index + 1}</span>
                  <div>
                    <h3>{group.title}</h3>
                    <ul>{group.items.map((occasion) => <li key={occasion}>{occasion}</li>)}</ul>
                  </div>
                </div>
              ))}
            </div>
            <p className="events-variety__closing">Dein Anlass ist nicht aufgeführt? Sprich mit uns. Wir entwickeln das passende Konzept für dein Event.</p>
          </div>
        </section>

        <section className="events-massage" aria-labelledby="events-massage-title">
          <div className="events-container">
            <div className="events-section-heading">
              <span className="events-eyebrow">Flexibel im Ablauf</span>
              <h2 id="events-massage-title">Massage als Teil deines Events</h2>
              <p>Eine Massage muss nicht bedeuten, dass sich deine Gäste zurückziehen, umziehen oder viel Zeit einplanen müssen. Unsere Perkussionsmassage wurde dafür entwickelt, sich unkompliziert in den Ablauf einer Veranstaltung integrieren zu lassen.</p>
              <p className="events-massage__statement">In Alltagskleidung. Im Stehen. Im Sitzen. Und je nach Anwendung sogar in Bewegung.</p>
              <p>Damit lässt sich unsere Massage genau dort einsetzen, wo neue Energie gebraucht wird.</p>
            </div>
            <ol className="events-moment-list">
              {EVENT_MOMENTS.map((moment, index) => (
                <li className="events-moment" key={moment.title}>
                  <span className="events-moment__number" aria-hidden="true">0{index + 1}</span>
                  <div><h3>{moment.title}</h3><p>{moment.text}</p></div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="events-offers" aria-labelledby="events-offers-title">
          <div className="events-container">
            <span className="events-eyebrow">Passend zu deinem Event</span>
            <h2 id="events-offers-title">Wake-Up+, Classic+ oder Intense+</h2>
            <p className="events-section-lead">Wir stellen die Massageangebote passend zu deinem Event zusammen.</p>
            <div className="events-offers__grid">
              {MASSAGE_OFFERS.map((offer, index) => (
                <article className="events-offer-card" key={offer.title}>
                  <span className="events-offer-card__index" aria-hidden="true">0{index + 1} / 03</span>
                  <h3>{offer.title}</h3>
                  <p>{offer.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="events-plus" aria-labelledby="events-plus-title">
          <div className="events-container">
            <div className="events-section-heading">
              <span className="events-eyebrow">Events+</span>
              <h2 id="events-plus-title">Mehr als Massage</h2>
              <p>Wir können dein Event nicht nur mit Massagen begleiten. Auf Wunsch entwickeln wir daraus ein komplettes Erlebnis.</p>
            </div>
            <div className="events-plus__features">
              <details className="events-plus-feature" open>
                <summary><span className="events-plus-feature__number">01</span><span className="events-plus-feature__title">Foto-Paket</span><span className="events-plus-feature__teaser">Momente festhalten</span><span className="events-plus-feature__toggle" aria-hidden="true">+</span></summary>
                <div className="events-plus-feature__body">
                  <p>Wir dokumentieren besondere Momente deiner Veranstaltung professionell. Je nach gebuchtem Paket stellen wir dafür Personal und entsprechendes Kameraequipment bereit.</p>
                  <p>So entstehen Bilder, die du später für deine interne Kommunikation, deine Website oder deine Social-Media-Kanäle nutzen kannst.</p>
                </div>
              </details>
              <details className="events-plus-feature">
                <summary><span className="events-plus-feature__number">02</span><span className="events-plus-feature__title">Video & Social Media</span><span className="events-plus-feature__teaser">Content, der bleibt</span><span className="events-plus-feature__toggle" aria-hidden="true">+</span></summary>
                <div className="events-plus-feature__body">
                  <p>Mit unserem Content-Paket halten wir die Veranstaltung in professionellen Foto- und Videoaufnahmen fest. Je nach Veranstaltung und gebuchtem Umfang arbeiten wir unter anderem mit Videokamera, Mikrofonen, Stativen und professioneller Drohnentechnik.</p>
                  <p>Auch dynamische Aufnahmen und – sofern Ort, Veranstaltung und rechtliche Rahmenbedingungen dies zulassen – Aufnahmen mit Tracking- beziehungsweise Verfolgungsdrohnen können Teil des Konzepts sein.</p>
                  <p>Auf Wunsch unterstützen wir dich auch bei Vorbereitung, Content-Erstellung und Veröffentlichung für Social Media. So wird aus deinem Event Content, der bleibt.</p>
                </div>
              </details>
              <details className="events-plus-feature">
                <summary><span className="events-plus-feature__number">03</span><span className="events-plus-feature__title">Event-Bus & Shuttle</span><span className="events-plus-feature__teaser">Mobil und sichtbar</span><span className="events-plus-feature__toggle" aria-hidden="true">+</span></summary>
                <div className="events-plus-feature__body">
                  <p>Durch individuelle Beschriftungs- und Gestaltungsmöglichkeiten wird aus unserem Fahrzeug auf Wunsch dein Event-Bus: ein sichtbarer Teil deiner Veranstaltung, ein Treffpunkt oder Bestandteil eines mobilen Eventkonzepts.</p>
                  <p>Auf Wunsch kombinieren wir ihn mit einem Shuttle-Service. Gerade bei Roadshows, Firmenveranstaltungen, Messen oder Veranstaltungen mit mehreren Standorten entsteht damit ein durchgängiger Auftritt.</p>
                </div>
              </details>
            </div>
          </div>
        </section>

        <section className="events-package" aria-labelledby="events-package-title">
          <div className="events-container">
            <div className="events-package__panel">
              <h2 id="events-package-title">Ein Team. Ein Konzept.<br />Ein Ansprechpartner.</h2>
              <p>Du musst die einzelnen Bestandteile nicht selbst miteinander koordinieren. Wir entwickeln gemeinsam mit dir ein Konzept und stimmen unsere Leistungen auf den Ablauf deiner Veranstaltung ab.</p>
              <ul className="events-package__grid">
                {SERVICES.map((service) => <li key={service}>{service}</li>)}
              </ul>
              <p className="events-package__closing">Alles kann individuell zusammengestellt werden. Vom kleinen Firmenmeeting bis zur großen Veranstaltung. Von Hamburg bis international.</p>
            </div>
          </div>
        </section>

        <section className="events-closing" aria-labelledby="events-closing-title">
          <div className="events-container">
            <h2 id="events-closing-title">Kümmere du dich um deine Gäste.<br />Wir kümmern uns um den Rest.</h2>
            <p>Dein Event soll Menschen zusammenbringen, überraschen und in Erinnerung bleiben. Genau dabei unterstützen wir dich: Wir begrüßen deine Gäste, schaffen zwischendurch aktivierende Momente, begleiten dein Programm und halten die besonderen Augenblicke auf Wunsch professionell fest.</p>
            <p className="events-closing__signature">Dr. Hoffmann Events+. Massage. Energie. Event. Content. Immer und überall.</p>
            <a href="#event-anfrage" className="events-closing__cta">Event anfragen <span aria-hidden="true">→</span></a>
          </div>
        </section>

        <section className="events-inquiry" id="event-anfrage" aria-labelledby="events-inquiry-title">
          <div className="events-container events-inquiry__layout">
            <div className="events-inquiry__intro">
              <h2 id="events-inquiry-title">Lass uns über dein Event sprechen</h2>
              <p>Ob kleines Firmenmeeting oder große Veranstaltung: Erzähl uns, was du planst. Gemeinsam entwickeln wir ein Konzept und stimmen Massage, Eventbegleitung und zusätzliche Leistungen auf deinen Ablauf ab.</p>
              <ul className="events-inquiry__features">
                <li><strong>Individuell geplant</strong><span>Passend zu deiner Veranstaltung und deinen Gästen.</span></li>
                <li><strong>Flexibel kombinierbar</strong><span>Massage, Foto, Video, Social Media, Event-Bus und Shuttle-Service auf Wunsch.</span></li>
                <li><strong>Ein Ansprechpartner</strong><span>Wir stimmen die gebuchten Leistungen auf deinen Eventablauf ab.</span></li>
              </ul>
            </div>
            <form ref={formRef} className="events-inquiry__form" onSubmit={handleSubmit} onFocusCapture={centerFocusedField} onBlurCapture={advanceOnCompletedBlur} onKeyDownCapture={advanceOnEnter}>
              <div className="events-inquiry__fields">
                <label>Dein Name *<input data-event-field="name" required value={form.name} onChange={updateForm("name")} autoComplete="name" /></label>
                <label>Unternehmen / Organisation (falls vorhanden)<input data-event-field="company" value={form.company} onChange={updateForm("company")} autoComplete="organization" /></label>
                <label>Deine E-Mail-Adresse *<input data-event-field="email" type="email" required value={form.email} onChange={updateForm("email")} autoComplete="email" /></label>
                <label>Telefonnummer für Rückfragen<input data-event-field="phone" type="tel" value={form.phone} onChange={updateForm("phone")} autoComplete="tel" /></label>
                <label>Wunschdatum (falls bekannt)<input data-event-field="desiredDate" type="date" value={form.desiredDate} onChange={updateForm("desiredDate")} /></label>
                <label>Eventformat und Ort *<input data-event-field="eventType" required value={form.eventType} onChange={updateForm("eventType")} placeholder="z. B. Workshop in Hamburg" /></label>
                <label>Voraussichtliche Gästezahl<input data-event-field="personCount" type="number" min="1" value={form.personCount} onChange={updateForm("personCount")} /></label>
                <label className="events-inquiry__message">Dein Event und deine Wünsche<textarea data-event-field="message" rows="4" value={form.message} onChange={updateForm("message")} placeholder="Was planst du? Welche Leistungen wünschst du dir?" /></label>
              </div>
              <label className="events-inquiry__privacy"><input data-event-field="acceptedContact" type="checkbox" checked={form.acceptedContact} onChange={updateForm("acceptedContact")} /><span>Ich stimme der Kontaktaufnahme zu meiner Eventanfrage gemäß der <Link to="/datenschutz">Datenschutzerklärung</Link> zu.</span></label>
              {feedback && <p className={`events-inquiry__feedback is-${feedback.type}`} role="status">{feedback.message}</p>}
              <button data-event-field="submit" className="events-inquiry__submit" type="submit" disabled={submitting}>{submitting ? "Anfrage wird gesendet..." : "Eventanfrage senden →"}</button>
            </form>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default EventsPage;
